import { forwardRef, useImperativeHandle, useRef } from "react";
import { createPortal } from "react-dom";
import { C_ON_SURFACE, C_OUTLINE } from "~/common/components/icon/Colors";
import { GlobalStyles } from "~/common/mui";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "zoovu-zoe": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        region?: string;
        "api-key"?: string;
        locale?: string;
        sku?: string;
        mode?: string;
        display?: string;
        feedback?: string;
      };
    }
  }
}

interface ZoovuZoeProps {
  region?: string;
  locale?: string;
  sku?: string;
  display?: "on-page" | "popup";
}

export interface ZoovuZoeRef {
  open: () => void;
}

export const ZoovuZoe = forwardRef<ZoovuZoeRef, ZoovuZoeProps>(({ display = "on-page", ...props }, ref) => {
  const zoeRef = useRef<HTMLElement>(null);
  const { sku } = props;

  useImperativeHandle(
    ref,
    () => ({
      open() {
        zoeRef.current?.dispatchEvent(new Event("show-zoovu-zoe"));
      },
    }),
    [],
  );

  const zoovuZoe = (
    <>
      <GlobalStyles
        styles={(theme) => ({
          "zoovu-zoe::part(conversation)": {
            "--base-color-inverted": theme.palette.text.primary,
            overflowX: "hidden",
          },
          "zoovu-zoe": {
            "--primary-color": C_OUTLINE,
            width: display == "popup" ? 0 : "auto",
          },
          "zoovu-zoe::part(popup)": {
            position: "fixed",
            margin: "auto",
            transformOrigin: "0 100%",
            maxWidth: display == "popup" ? "780px" : "auto",
            minHeight: display == "popup" ? "calc(100% - 52px)" : "auto",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: "white",
            borderRadius: "28px",
            color: theme.palette.textSecondary,
            padding: 0,

            [theme.breakpoints.down("tablet")]: {
              border: `none !important`,
              height: "80vh !important",
              minHeight: "80vh !important",
              maxHeight: "80vh !important",
              bottom: "0 !important",
              top: "auto !important",
              left: "0 !important",
              right: "0 !important",
              margin: "0 !important",
              transformOrigin: "center bottom !important",
              borderRadius: "28px 28px 0 0 !important",
            },
          },
          "zoovu-zoe::part(title)": {
            color: theme.palette.text.secondary,
          },
          "zoovu-zoe::part(input)": {
            color: theme.palette.textSecondary,
            borderRadius: "4px",
            border: `1px solid ${theme.palette.divider}`,
            height: "56px",
          },
          "zoovu-zoe::part(input)::placeholder": {
            color: theme.palette.textLabel.main,
          },
          "zoovu-zoe::part(popup-button)": {
            display: "none",
          },
          "zoovu-zoe::part(popup-backdrop)": {
            opacity: 0.6,
            backgroundColor: C_ON_SURFACE,
          },
          "zoovu-zoe::part(chat)": {
            gap: "0px",
          },
          "zoovu-zoe::part(chat-message)": {
            borderRadius: "8px",
            color: theme.palette.text.secondary,
          },
          "zoovu-zoe::part(user-message)": {
            backgroundColor: theme.palette.primary.container,
          },
          "zoovu-zoe::part(zoe-message)": {
            backgroundColor: "white",
            paddingLeft: 0,
          },
          "zoovu-zoe::part(feedback)": {
            paddingLeft: 0,
          },
          "zoovu-zoe::part(actions-option)": {
            borderRadius: "8px",
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.primary.container,
            borderColor: theme.palette.divider,
          },
          "zoovu-zoe::part(suggested-questions)": {
            marginRight: "calc(var(--conversation-indent) * -1)", //parent of this class does not have "part"
            flexDirection: "column",
            alignItems: "flex-end",
          },
          "zoovu-zoe::part(product)": {
            backgroundColor: "white",
          },
          "zoovu-zoe::part(disclaimer)": {
            textAlign: "center",
          },
        })}
      />

      <zoovu-zoe
        key={sku}
        ref={zoeRef}
        api-key="GyTg30hsAZ1366yeQnMSwqDDppA"
        mode="full"
        display={display}
        feedback="true"
        sku={sku}
        {...props}
      />
    </>
  );

  if (display == "on-page") {
    return zoovuZoe;
  }

  return createPortal(zoovuZoe, document.body);
});
